import React from 'react';

import StepHeader from '../../design-system/StepHeader';
import Button from '../../design-system/Button';
import { spacing } from '../../design-system/tokens';
import OrderSummaryCard from './OrderSummaryCard';
import type { OrderSummaryItem } from './OrderSummaryCard';

/**
 * LocationReview — "Resumo do pedido" de UM local (Fase 2 do fluxo), dirigido
 * pelo container. Diferente de `OrderReviewSection` (que itera os locais com
 * índice interno), aqui o container controla qual base está em foco — por isso
 * recebe `counter`/`isLast`/`multi` explícitos.
 *
 * Fluxo: o formulário do local avança para este resumo; daqui o usuário "Edita
 * dados do local" (volta ao formulário) ou avança para o próximo local. No
 * último local, o botão vira "Finalizar pedido(s)" e leva ao resumo geral.
 */
export interface LocationReviewProps {
  /** Nome do local (ex.: "Refeitório fundos"). */
  title: string;
  /** Contador "Local x de y" — omitido quando há um único local. */
  counter?: string;
  /** Itens do resumo (vêm do adapter `toLocationSummaries`). */
  items: OrderSummaryItem[];
  /** É o último local do fluxo? Define o rótulo do botão primário. */
  isLast: boolean;
  /** Há mais de um local? Define "Finalizar pedidos" vs "Finalizar pedido". */
  multi: boolean;
  /** Volta ao formulário para editar este local. */
  onEdit: () => void;
  /** Avança: próximo local (ou resumo geral, se for o último). */
  onNext: () => void;
}

const LocationReview = ({
  title,
  counter,
  items,
  isLast,
  multi,
  onEdit,
  onNext,
}: LocationReviewProps): JSX.Element => {
  const finishLabel = multi ? 'Finalizar pedidos' : 'Finalizar pedido';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <StepHeader
        title={title}
        description="preencha os dados abaixo"
        state="active"
        counter={counter}
      />

      <OrderSummaryCard title="Resumo do pedido" items={items} />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing.md,
          flexWrap: 'wrap',
        }}
      >
        <Button variant="secondary" shape="pill" size="xl" onClick={onEdit}>
          Editar dados do local
        </Button>
        <Button variant="primary" shape="pill" size="xl" onClick={onNext}>
          {isLast ? finishLabel : 'Ir para a próxima central de abastecimento'}
        </Button>
      </div>
    </div>
  );
};

export default LocationReview;
